import { useEffect, useRef, useState } from 'react'
import { Link, NavLink as RouterNavLink, useLocation } from 'react-router'
import { AnimatePresence, motion } from 'motion/react'
import { Menu, X } from 'lucide-react'
import { useNavLinks, useSettings } from '@/lib/queries'
import { cn } from '@/lib/utils'
import { Crest, Container } from '@/components/site/primitives'
import { AnnouncementBar } from './announcement-bar'

/**
 * The header is transparent over the hero and turns to solid ink once the page
 * scrolls, so the wordmark never fights the photograph behind it.
 *
 * Nav items come from the `nav_links` collection — the club can rename, reorder
 * or hide any of them without a deploy.
 */
export function SiteHeader() {
  const { data: settings } = useSettings()
  const { data: links = [] } = useNavLinks()
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()

  const announcementRef = useRef<HTMLDivElement>(null)
  const navRowRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  /**
   * Publish the header's full height as `--header-h` so page heroes can clear
   * it exactly. A hardcoded padding is not enough: the announcement bar is
   * editable content that wraps to two lines on a phone, and heroes would end
   * up with the wordmark printed through the club name.
   *
   * The announcement's `scrollHeight` is used rather than its rendered height,
   * because it collapses to zero on scroll — the clearance must stay constant
   * or the page would jump as it collapses.
   */
  useEffect(() => {
    const publish = () => {
      const announcement = announcementRef.current?.scrollHeight ?? 0
      const navRow = navRowRef.current?.offsetHeight ?? 0
      // Measure the tall (unscrolled) nav row even while scrolled, so the value
      // never shrinks mid-page.
      const tallest = Math.max(navRow, scrolled ? 96 : navRow)
      document.documentElement.style.setProperty('--header-h', `${announcement + tallest}px`)
    }

    publish()
    const observer = new ResizeObserver(publish)
    if (announcementRef.current) observer.observe(announcementRef.current)
    if (navRowRef.current) observer.observe(navRowRef.current)
    window.addEventListener('resize', publish)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', publish)
    }
  }, [scrolled, links.length, settings?.announcement_text, settings?.announcement_active])

  // Close the drawer on navigation, including back/forward.
  useEffect(() => setMenuOpen(false), [location.pathname])

  // A fixed-position drawer over a scrollable body causes the page behind it to
  // scroll on touch devices; lock it while the drawer is open.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [menuOpen])

  const clubName = settings?.club_name ?? 'AFC Vikings Cairo'

  return (
    <>
      <a
        href="#main"
        className="focus:bg-blood focus:text-bone sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-100 focus:px-4 focus:py-2"
      >
        Skip to content
      </a>

      <header
        className={cn(
          'fixed inset-x-0 top-0 z-50 transition-all duration-500',
          scrolled
            ? 'border-hairline bg-ink/92 border-b backdrop-blur-xl'
            : 'from-ink/80 border-b border-transparent bg-gradient-to-b to-transparent'
        )}
      >
        {/* Hidden once the page scrolls, so the header stays compact while
            reading and the announcement still greets a first-time visitor.
            The max-height is generous because the text is editable and wraps to
            two lines on a phone — clipping it would cut an editor's copy. */}
        <div
          ref={announcementRef}
          className={cn(
            'overflow-hidden transition-[max-height,opacity] duration-500',
            scrolled ? 'max-h-0 opacity-0' : 'max-h-40 opacity-100'
          )}
        >
          <AnnouncementBar />
        </div>

        <Container size="wide">
          <div
            ref={navRowRef}
            className={cn(
              'flex items-center justify-between gap-6 transition-all duration-500',
              scrolled ? 'h-16' : 'h-20 md:h-24'
            )}
          >
            <Link
              to="/"
              className="group flex min-w-0 items-center gap-3 focus-visible:outline-offset-4"
              aria-label={`${clubName} — home`}
            >
              <Crest
                className={cn(
                  'shrink-0 transition-all duration-500',
                  scrolled ? 'size-9' : 'size-11 md:size-14'
                )}
              />
              <span className="condensed text-bone group-hover:text-blood-bright truncate text-[0.9375rem] font-bold tracking-[0.14em] transition-colors md:text-lg">
                {clubName}
              </span>
            </Link>

            <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
              {links.map((link) =>
                link.external ? (
                  <a
                    key={link.id}
                    href={link.path}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="condensed text-bone-dim hover:text-bone px-4 py-2 text-sm font-semibold tracking-[0.16em] transition-colors"
                  >
                    {link.label}
                  </a>
                ) : (
                  <RouterNavLink
                    key={link.id}
                    to={link.path}
                    className={({ isActive }) =>
                      cn(
                        'condensed relative px-4 py-2 text-sm font-semibold tracking-[0.16em] transition-colors',
                        isActive ? 'text-bone' : 'text-bone-dim hover:text-bone'
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {link.label}
                        <span
                          className={cn(
                            'bg-blood absolute inset-x-4 -bottom-0.5 h-0.5 origin-left transition-transform duration-300',
                            isActive ? 'scale-x-100' : 'scale-x-0'
                          )}
                          aria-hidden
                        />
                      </>
                    )}
                  </RouterNavLink>
                )
              )}
            </nav>

            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="border-hairline text-bone hover:border-blood hover:text-blood-bright flex size-11 items-center justify-center border transition-colors lg:hidden"
              aria-label="Open menu"
              aria-expanded={menuOpen}
            >
              <Menu className="size-5" aria-hidden />
            </button>
          </div>
        </Container>
      </header>

      <AnimatePresence>
        {menuOpen ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="bg-ink fixed inset-0 z-100 lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
          >
            <div className="flex h-20 items-center justify-between px-5">
              <Crest className="size-11" />
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="border-hairline text-bone flex size-11 items-center justify-center border"
                aria-label="Close menu"
                autoFocus
              >
                <X className="size-5" aria-hidden />
              </button>
            </div>

            <nav className="flex flex-col px-5 pt-6" aria-label="Mobile">
              {links.map((link, index) => (
                <motion.div
                  key={link.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + index * 0.05, ease: [0.16, 1, 0.3, 1] }}
                >
                  <RouterNavLink
                    to={link.path}
                    className={({ isActive }) =>
                      cn(
                        'display border-hairline flex items-baseline gap-4 border-b py-5 text-[2.5rem] leading-none transition-colors',
                        isActive ? 'text-blood' : 'text-bone hover:text-blood-bright'
                      )
                    }
                  >
                    <span className="condensed text-bone-faint text-xs tracking-[0.2em]">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    {link.label}
                  </RouterNavLink>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  )
}
